import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import {
  History, User, PackagePlus, PackageCheck, Pencil, RefreshCw, ArrowRight, XCircle, Truck, Send, ShieldCheck, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const ICONS = {
  created:       { Icon: PackagePlus, tint: "bg-orange-600", label: "Created" },
  dispatched:    { Icon: Send,        tint: "bg-orange-600", label: "Dispatched" },
  in_transit:    { Icon: Truck,       tint: "bg-blue-600",   label: "In Transit" },
  completed:     { Icon: ShieldCheck, tint: "bg-emerald-600",label: "Completed" },
  cancelled:     { Icon: XCircle,     tint: "bg-zinc-500",   label: "Cancelled" },
  updated_and_transmitted: { Icon: Pencil, tint: "bg-violet-600", label: "Edited & Transmitted" },
  updated_silent:{ Icon: Pencil,      tint: "bg-zinc-500",   label: "Edited (silent)" },
  received_in:   { Icon: PackageCheck,tint: "bg-emerald-600",label: "Received In" },
  receival_cleared: { Icon: RefreshCw, tint: "bg-zinc-500",  label: "Receival Cleared" },
  status_update: { Icon: RefreshCw,   tint: "bg-blue-600",   label: "Status Update" },
};

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function AuditLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit", { params: { limit: 200 } });
        setEvents(data.events || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = filter
    ? events.filter((e) => `${e.by} ${e.reference_number} ${e.pickup_company || ""} ${e.delivery_company || ""}`.toLowerCase().includes(filter.toLowerCase()))
    : events;

  const csvEscape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const exportCsv = () => {
    const header = ["When", "By", "Action", "Reference", "Type", "From", "To", "Detail"];
    const rows = filtered.map((e) => [
      e.at || "",
      e.by || "",
      (e.action || "").replace(/_/g, " "),
      e.reference_number || "",
      e.request_type || "",
      e.pickup_company || "",
      e.delivery_company || "",
      e.detail || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `out-and-in-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="border-2 border-foreground p-2">
          <LogoMark size={40} className="text-foreground" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
            Out <span className="text-orange-600">&amp;</span> In &middot; Team Activity
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Audit Log</h1>
        </div>
      </div>

      <div className="border-2 border-border bg-card p-4 flex items-center gap-3 flex-wrap">
        <input
          type="text"
          data-testid="audit-filter"
          placeholder="Filter by teammate, reference, company..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex-1 min-w-[200px] h-10 px-3 rounded-none border-2 border-border focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
        />
        <Button
          type="button"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="audit-export-csv"
          className="rounded-none bg-orange-600 hover:bg-orange-700 h-10 uppercase tracking-[0.15em] text-xs font-bold"
        >
          <Download className="h-3.5 w-3.5 mr-2" /> Export CSV
          <span className="ml-2 text-white/70 font-normal normal-case tracking-normal">({filtered.length})</span>
        </Button>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading activity…</div>}
      {!loading && filtered.length === 0 && (
        <div className="border-2 border-border bg-card p-10 text-center text-sm text-muted-foreground">
          <History className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No matching activity yet.
        </div>
      )}

      <div className="relative">
        <div className="absolute left-4 sm:left-5 top-0 bottom-0 w-0.5 bg-border" aria-hidden />
        <ul className="space-y-3">
          {filtered.map((e, i) => {
            const meta = ICONS[e.action] || { Icon: History, tint: "bg-zinc-500", label: (e.action || "").replace(/_/g, " ") };
            const Icon = meta.Icon;
            return (
              <li key={i} data-testid={`audit-event-${i}`} className="relative pl-10 sm:pl-14">
                <div className={`absolute left-0 top-1 h-8 w-8 sm:h-10 sm:w-10 flex items-center justify-center text-white ${meta.tint}`}>
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="border-2 border-border bg-card p-3 sm:p-4">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 border border-border">
                      <User className="h-3 w-3" /> {e.by}
                    </span>
                    <span className={`text-[10px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 text-white ${meta.tint}`}>
                      {meta.label}
                    </span>
                    <span className="text-muted-foreground text-[11px]">{fmtWhen(e.at)}</span>
                  </div>
                  <div className="mt-2 text-sm flex items-center gap-2 flex-wrap">
                    <Link to={`/requests/${e.request_id}`} className="font-mono font-bold hover:text-orange-600">
                      {e.reference_number}
                    </Link>
                    <span className="text-muted-foreground">&middot;</span>
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">{e.request_type}</span>
                    {(e.pickup_company || e.delivery_company) && (
                      <>
                        <span className="text-muted-foreground">&middot;</span>
                        <span className="text-xs">{e.pickup_company || "—"} <ArrowRight className="inline h-3 w-3 mx-1" /> {e.delivery_company || "—"}</span>
                      </>
                    )}
                  </div>
                  {e.detail && <div className="mt-1 text-xs text-muted-foreground">{e.detail}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
